import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Unlink, Undo, Redo, Heading2, Pilcrow, Image as ImageIcon, Code2, Eye,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';

const isFullDoc = (html) => /<html[\s>]/i.test(html || '');

const buildDoc = (html) => (isFullDoc(html) ? html : `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:16px;color:#333;line-height:1.5}
img{max-width:100%}
</style></head><body>${html || ''}</body></html>`);

const serialize = (doc, full) => (full ? `<!DOCTYPE html>\n${doc.documentElement.outerHTML}` : doc.body.innerHTML);

const ToolBtn = ({ label, onClick, children }) => (
  <Button type="button" size="sm" variant="ghost" className="h-8 px-2" title={label} aria-label={label}
    data-testid={`email-editor-${label.toLowerCase().replace(/\s+/g, '-')}`}
    onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
    {children}
  </Button>
);

const UrlPopover = ({ label, icon, placeholder, onApply }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const apply = () => { if (url) onApply(url); setUrl(''); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="ghost" className="h-8 px-2" title={label} aria-label={label}
          data-testid={`email-editor-${label.toLowerCase().replace(/\s+/g, '-')}`}>{icon}</Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2">
        <Input type="url" placeholder={placeholder} value={url} onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()} data-testid={`email-editor-${label.toLowerCase().replace(/\s+/g, '-')}-input`} />
        <Button type="button" size="sm" onClick={apply}>Insert</Button>
      </PopoverContent>
    </Popover>
  );
};

const EmailEditor = ({ value, onChange }) => {
  const [mode, setMode] = useState('visual');
  const frameRef = useRef(null);
  const lastHtml = useRef(null);
  const fullRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const emit = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.body) return;
    const html = serialize(doc, fullRef.current);
    lastHtml.current = html;
    onChangeRef.current?.(html);
  }, []);

  useEffect(() => {
    if (mode !== 'visual') return;
    const doc = frameRef.current?.contentDocument;
    if (!doc || (value === lastHtml.current && doc.body)) return;
    fullRef.current = isFullDoc(value);
    lastHtml.current = value;
    doc.open();
    doc.write(buildDoc(value));
    doc.close();
    doc.designMode = 'on';
    doc.addEventListener('input', emit);
  }, [value, mode, emit]);

  const exec = (cmd, arg = null) => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    frameRef.current.contentWindow.focus();
    doc.execCommand(cmd, false, arg);
    emit();
  };

  const switchMode = (m) => { lastHtml.current = null; setMode(m); };

  return (
    <div className="email-editor" data-testid="email-editor">
      <div className="border border-b-0 rounded-t-md bg-slate-50 p-2 flex flex-wrap gap-1 items-center">
        <div className="flex rounded-md border bg-white mr-2 overflow-hidden">
          <button type="button" data-testid="email-editor-visual-tab" onClick={() => switchMode('visual')}
            className={`px-3 h-8 text-xs font-medium flex items-center gap-1 ${mode === 'visual' ? 'bg-[#2C9DE1] text-white' : 'text-slate-600'}`}>
            <Eye className="w-3.5 h-3.5" /> Visual
          </button>
          <button type="button" data-testid="email-editor-html-tab" onClick={() => switchMode('html')}
            className={`px-3 h-8 text-xs font-medium flex items-center gap-1 ${mode === 'html' ? 'bg-[#2C9DE1] text-white' : 'text-slate-600'}`}>
            <Code2 className="w-3.5 h-3.5" /> HTML
          </button>
        </div>
        {mode === 'visual' && (
          <>
            <ToolBtn label="Bold" onClick={() => exec('bold')}><Bold className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Italic" onClick={() => exec('italic')}><Italic className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Underline" onClick={() => exec('underline')}><Underline className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Heading" onClick={() => exec('formatBlock', 'h2')}><Heading2 className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Paragraph" onClick={() => exec('formatBlock', 'p')}><Pilcrow className="h-4 w-4" /></ToolBtn>
            <Separator orientation="vertical" className="h-6 mx-1" />
            <ToolBtn label="Align left" onClick={() => exec('justifyLeft')}><AlignLeft className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Align center" onClick={() => exec('justifyCenter')}><AlignCenter className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Align right" onClick={() => exec('justifyRight')}><AlignRight className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Bullet list" onClick={() => exec('insertUnorderedList')}><List className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Numbered list" onClick={() => exec('insertOrderedList')}><ListOrdered className="h-4 w-4" /></ToolBtn>
            <Separator orientation="vertical" className="h-6 mx-1" />
            <UrlPopover label="Add link" icon={<LinkIcon className="h-4 w-4" />} placeholder="https://empmedic.com/..." onApply={(u) => exec('createLink', u)} />
            <ToolBtn label="Remove link" onClick={() => exec('unlink')}><Unlink className="h-4 w-4" /></ToolBtn>
            <UrlPopover label="Insert image" icon={<ImageIcon className="h-4 w-4" />} placeholder="https://.../image.png" onApply={(u) => exec('insertImage', u)} />
            <Separator orientation="vertical" className="h-6 mx-1" />
            <ToolBtn label="Undo" onClick={() => exec('undo')}><Undo className="h-4 w-4" /></ToolBtn>
            <ToolBtn label="Redo" onClick={() => exec('redo')}><Redo className="h-4 w-4" /></ToolBtn>
            <Separator orientation="vertical" className="h-6 mx-1" />
            {[['First Name', '{{first_name}}'], ['Last Name', '{{last_name}}'], ['Full Name', '{{full_name}}']].map(([l, tok]) => (
              <Button key={tok} type="button" size="sm" variant="outline" onMouseDown={(e) => e.preventDefault()}
                onClick={() => exec('insertText', tok)} data-testid={`email-editor-token-${tok.replace(/[{}]/g, '')}`}
                className="h-7 text-xs px-2 bg-blue-50 hover:bg-blue-100 border-blue-200">{l}</Button>
            ))}
          </>
        )}
      </div>
      <div className="border rounded-b-md bg-white">
        {mode === 'visual' ? (
          <iframe ref={frameRef} title="Email editor" data-testid="email-editor-visual" className="w-full h-[520px] block" />
        ) : (
          <Textarea data-testid="email-editor-html" value={value || ''} onChange={(e) => onChange?.(e.target.value)}
            className="font-mono text-xs h-[520px] border-0 rounded-none rounded-b-md" spellCheck={false} />
        )}
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Click any text in the email to edit it. Images, buttons and layout are kept as they are.
      </p>
    </div>
  );
};

export default EmailEditor;
